import React, { useState, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Table,
  Form,
  InputGroup,
  Modal,
  Toast,
  ToastContainer,
  Spinner,
  Pagination,
  Alert,
} from 'react-bootstrap';
import {
  Shield,
  Search,
  InfoCircle,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  ExclamationTriangle,
  BarChart,
  ChevronDown,
  ChevronUp,
} from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/AuthContext';

// Données fictives des alertes (inchangées)
const mockAlerts = [
  { id: 'F001', date: '2025-03-15', type: 'Prescription suspecte', description: 'Plusieurs prescriptions identiques pour un même patient', severity: 'medium', status: 'new' },
  { id: 'F002', date: '2025-03-14', type: 'Double remboursement', description: 'Demande de remboursement en double pour la même ordonnance', severity: 'high', status: 'new' },
  { id: 'F003', date: '2025-03-13', type: 'Faux document', description: 'Document falsifié détecté lors de la soumission', severity: 'high', status: 'investigating' },
  { id: 'F004', date: '2025-03-12', type: 'Comportement suspect', description: 'Plusieurs consultations chez différents médecins en peu de temps', severity: 'medium', status: 'new' },
  { id: 'F005', date: '2025-03-11', type: 'Anomalie de facturation', description: 'Montant anormalement élevé pour des soins standards', severity: 'low', status: 'investigating' },
  { id: 'F006', date: '2025-03-10', type: 'Prescription suspecte', description: 'Médicaments incompatibles prescrits ensemble', severity: 'medium', status: 'resolved' },
  { id: 'F007', date: '2025-03-09', type: 'Faux document', description: 'Certificat médical suspect', severity: 'high', status: 'new' },
  { id: 'F008', date: '2025-03-08', type: 'Double remboursement', description: 'Remboursement déjà effectué', severity: 'low', status: 'resolved' },
];

const FraudAlerts = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState(mockAlerts);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showExamineModal, setShowExamineModal] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    const newSet = new Set(expandedRows);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setExpandedRows(newSet);
  };

  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  };

  // Filtrage
  const filteredAlerts = useMemo(() => {
    let filtered = alerts;
    if (severityFilter !== 'all') filtered = filtered.filter(a => a.severity === severityFilter);
    if (statusFilter !== 'all') filtered = filtered.filter(a => a.status === statusFilter);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(a =>
        a.type.toLowerCase().includes(term) ||
        a.description.toLowerCase().includes(term) ||
        a.id.toLowerCase().includes(term)
      );
    }
    return filtered;
  }, [alerts, searchTerm, severityFilter, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredAlerts.length / itemsPerPage);
  const paginatedAlerts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAlerts.slice(start, start + itemsPerPage);
  }, [filteredAlerts, currentPage, itemsPerPage]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getSeverityBadge = (severity) => {
    const config = {
      high: { bg: 'danger', icon: <ExclamationTriangle size={12} className="me-1" /> },
      medium: { bg: 'warning', icon: <Clock size={12} className="me-1" /> },
      low: { bg: 'info', icon: <InfoCircle size={12} className="me-1" /> },
    };
    const { bg, icon } = config[severity] || { bg: 'secondary', icon: null };
    return <Badge bg={bg} className="rounded-pill px-3 py-2 d-inline-flex align-items-center">{icon} {severity}</Badge>;
  };

  const getStatusBadge = (status) => {
    const config = {
      new: { bg: 'danger', label: 'Nouveau', icon: <ExclamationTriangle size={12} className="me-1" /> },
      investigating: { bg: 'warning', label: 'En cours', icon: <Clock size={12} className="me-1" /> },
      resolved: { bg: 'success', label: 'Résolu', icon: <CheckCircle size={12} className="me-1" /> },
    };
    const { bg, label, icon } = config[status] || { bg: 'secondary', label: status, icon: null };
    return <Badge bg={bg} className="rounded-pill px-3 py-2 d-inline-flex align-items-center">{icon} {label}</Badge>;
  };

  // Statistiques
  const stats = {
    total: filteredAlerts.length,
    high: filteredAlerts.filter(a => a.severity === 'high').length,
    medium: filteredAlerts.filter(a => a.severity === 'medium').length,
    low: filteredAlerts.filter(a => a.severity === 'low').length,
    new: filteredAlerts.filter(a => a.status === 'new').length,
  };

  const statsCards = [
    { title: 'Total alertes', value: stats.total, icon: Shield, color: '#4361ee', gradient: 'linear-gradient(135deg, #4361ee 0%, #3b2a9f 100%)', bg: 'white' },
    { title: 'Niveau élevé', value: stats.high, icon: ExclamationTriangle, color: '#ef4444', gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', bg: 'white' },
    { title: 'Niveau moyen', value: stats.medium, icon: Clock, color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', bg: 'white' },
    { title: 'Nouveautés', value: stats.new, icon: InfoCircle, color: '#06b6d4', gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', bg: 'white' },
  ];

  const handleExamine = (alert) => {
    setSelectedAlert(alert);
    setShowExamineModal(true);
  };

  const updateAlertStatus = (newStatus) => {
    setLoading(true);
    setTimeout(() => {
      setAlerts(prev => prev.map(a => a.id === selectedAlert.id ? { ...a, status: newStatus } : a));
      showToast(`Alerte ${selectedAlert.id} marquée comme ${newStatus === 'investigating' ? 'en cours' : 'résolue'}`, 'success');
      setShowExamineModal(false);
      setSelectedAlert(null);
      setLoading(false);
    }, 800);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const items = [];
    for (let i = 1; i <= totalPages; i++) {
      items.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>{i}</Pagination.Item>);
    }
    return (
      <div className="d-flex justify-content-end mt-3">
        <Pagination size="sm">
          <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
          {items}
          <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
        </Pagination>
      </div>
    );
  };

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
        <Toast show={toast.show} onClose={() => setToast(prev => ({ ...prev, show: false }))} bg={toast.variant} delay={4000} autohide>
          <Toast.Header closeButton={false}>
            <strong className="me-auto">
              {toast.variant === 'success' ? 'Succès' : toast.variant === 'danger' ? 'Erreur' : 'Information'}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="container-fluid">
        {/* En-tête */}
        <div className="mb-4">
          <h4 className="main-title mb-0 d-flex align-items-center gap-2">
            <div className="bg-danger bg-opacity-10 rounded-circle p-2">
              <Shield size={24} className="text-danger" />
            </div>
            Alertes fraude
          </h4>
          <p className="text-muted mb-0 mt-1">Détectez et traitez les comportements suspects pour protéger le système</p>
        </div>

        {/* Cartes de statistiques */}
        <Row className="g-3 mb-4">
          {statsCards.map((card, idx) => (
            <Col md={6} lg={3} xs={6} key={idx}>
              <Card className="border-0 shadow-sm stats-card h-100" style={{ borderRadius: '16px', background: card.bg }}>
                <div className="card-gradient" style={{ background: card.gradient, height: '4px' }}></div>
                <Card.Body className="p-3 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="text-muted mb-1 small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px' }}>{card.title}</h6>
                      <h3 className="mb-0 fw-bold" style={{ fontSize: '1.6rem' }}>{card.value}</h3>
                    </div>
                    <div className="icon-container rounded-3 d-flex align-items-center justify-content-center" style={{ background: `${card.color}20`, width: '48px', height: '48px' }}>
                      <card.icon size={28} style={{ color: card.color }} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Barre de recherche et filtres */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4 bg-white">
            <Row className="align-items-center g-3">
              <Col md={5} xs={6}>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-0"><Search size={18} className="text-muted" /></InputGroup.Text>
                  <Form.Control type="text" placeholder="Rechercher par type ou description..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="border-0 bg-light" />
                </InputGroup>
              </Col>
              <Col md={3} xs={6}>
                <Form.Select value={severityFilter} onChange={(e) => { setSeverityFilter(e.target.value); setCurrentPage(1); }} className="border-0 bg-light">
                  <option value="all">Tous niveaux</option>
                  <option value="high">Élevé</option>
                  <option value="medium">Moyen</option>
                  <option value="low">Faible</option>
                </Form.Select>
              </Col>
              <Col md={2} xs={6}>
                <Form.Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} className="border-0 bg-light">
                  <option value="all">Tous statuts</option>
                  <option value="new">Nouveau</option>
                  <option value="investigating">En cours</option>
                  <option value="resolved">Résolu</option>
                </Form.Select>
              </Col>
              <Col md={2} xs={6}>
                <Form.Select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="border-0 bg-light">
                  <option value={5}>5 par page</option>
                  <option value={10}>10 par page</option>
                  <option value={20}>20 par page</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tableau des alertes */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
          <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
            <h5 className="fw-semibold mb-0">Liste des alertes</h5>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle table-striped">
                <thead className="bg-light">
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th className="d-none d-sm-table-cell">Description</th>
                    <th className="d-none d-sm-table-cell">Niveau</th>
                    <th className="d-none d-sm-table-cell">Statut</th>
                    <th className="d-none d-sm-table-cell">Actions</th>
                    <th className="d-sm-none">Détails</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAlerts.length > 0 ? (
                    paginatedAlerts.map(alert => {
                      const isExpanded = expandedRows.has(alert.id);
                      return (
                        <React.Fragment key={alert.id}>
                          {/* Ligne principale */}
                          <tr>
                            <td data-label="Date" className="small">{formatDate(alert.date)}</td>
                            <td data-label="Type" className="fw-medium">{alert.type}</td>
                            <td data-label="Description" className="d-none d-sm-table-cell">{alert.description}</td>
                            <td data-label="Niveau" className="d-none d-sm-table-cell">{getSeverityBadge(alert.severity)}</td>
                            <td data-label="Statut" className="d-none d-sm-table-cell">{getStatusBadge(alert.status)}</td>
                            <td data-label="Actions" className="d-none d-sm-table-cell">
                              <Button size="sm" variant="outline-primary" className="rounded-pill px-3" onClick={() => handleExamine(alert)} disabled={alert.status === 'resolved'}>
                                <Eye size={14} className="me-1" /> Examiner
                              </Button>
                            </td>
                            <td className="d-sm-none text-center" style={{ verticalAlign: 'middle' }}>
                              <Button variant="link" onClick={() => toggleRow(alert.id)} className="p-0 text-secondary" style={{ textDecoration: 'none' }}>
                                {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                              </Button>
                            </td>
                          </tr>
                          {/* Ligne de détails (visible seulement sur mobile et si dépliée) */}
                          {isExpanded && (
                            <tr className="d-sm-none detail-row">
                              <td colSpan="7" className="p-3 bg-light">
                                <div className="row g-2">
                                  <div className="col-12"><strong>Description :</strong> {alert.description}</div>
                                  <div className="col-6"><strong>Niveau :</strong> {getSeverityBadge(alert.severity)}</div>
                                  <div className="col-6"><strong>Statut :</strong> {getStatusBadge(alert.status)}</div>
                                  <div className="col-12">
                                    <strong>Actions :</strong>
                                    <Button size="sm" variant="outline-primary" className="rounded-pill mt-1" onClick={() => handleExamine(alert)} disabled={alert.status === 'resolved'}>
                                      <Eye size={14} className="me-1" /> Examiner
                                    </Button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-5">
                        <Shield size={48} className="text-muted mb-3" />
                        <p className="text-muted">Aucune alerte trouvée</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            {renderPagination()}
          </Card.Body>
        </Card>

        {/* Information */}
        <Row className="mt-4">
          <Col md={12}>
            <Alert variant="info" className="border-0 rounded-3 shadow-sm" style={{ background: 'linear-gradient(135deg, #e8f4fd 0%, #d9eaff 100%)' }}>
              <div className="d-flex align-items-center gap-3">
                <InfoCircle size={24} className="text-info" />
                <div>
                  <strong className="d-block mb-1">Traitement des alertes</strong>
                  <span className="small">Les alertes de fraude sont générées automatiquement. Examinez chaque alerte et passez-la en “En cours” puis “Résolu” après vérification.</span>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>

        {/* Modal d'examen (inchangé) */}
        <Modal show={showExamineModal} onHide={() => setShowExamineModal(false)} size="lg" centered>
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title className="d-flex align-items-center gap-2"><Eye size={20} /> Examiner l'alerte</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedAlert && (
              <>
                <div className="mb-3 p-3 rounded-3" style={{ background: '#fff3e0', borderLeft: '4px solid #f59e0b' }}>
                  <div className="d-flex align-items-center gap-2">
                    <ExclamationTriangle size={18} className="text-warning" />
                    <strong>Type :</strong> {selectedAlert.type}
                    <span className="text-muted">(ID: {selectedAlert.id})</span>
                  </div>
                </div>
                <Row className="mb-3">
                  <Col md={6}>
                    <div className="mb-3"><small className="text-muted d-block">Date</small><strong>{formatDate(selectedAlert.date)}</strong></div>
                    <div className="mb-3"><small className="text-muted d-block">Niveau</small>{getSeverityBadge(selectedAlert.severity)}</div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3"><small className="text-muted d-block">Statut actuel</small>{getStatusBadge(selectedAlert.status)}</div>
                  </Col>
                </Row>
                <div className="mb-3">
                  <small className="text-muted d-block">Description détaillée</small>
                  <div className="bg-light p-3 rounded-3">{selectedAlert.description}</div>
                </div>
                <Alert variant="warning" className="mt-2"><InfoCircle size={14} className="me-1" /> Si l'alerte est fondée, vous pouvez la marquer comme "En cours" puis "Résolu". Sinon, vous pouvez la rejeter (à implémenter).</Alert>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowExamineModal(false)} className="rounded-pill">Fermer</Button>
            {selectedAlert && selectedAlert.status === 'new' && (
              <Button variant="warning" onClick={() => updateAlertStatus('investigating')} disabled={loading} className="rounded-pill px-4">
                {loading ? <Spinner size="sm" className="me-1" /> : <Clock size={16} className="me-1" />}
                {loading ? 'Traitement...' : 'Marquer en cours'}
              </Button>
            )}
            {selectedAlert && selectedAlert.status === 'investigating' && (
              <Button variant="success" onClick={() => updateAlertStatus('resolved')} disabled={loading} className="rounded-pill px-4">
                {loading ? <Spinner size="sm" className="me-1" /> : <CheckCircle size={16} className="me-1" />}
                {loading ? 'Traitement...' : 'Marquer résolu'}
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        <style>{`
          .stats-card {
            position: relative;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 16px;
            overflow: hidden;
          }
          .stats-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
          }
          .card-gradient {
            transition: height 0.2s ease;
          }
          .stats-card:hover .card-gradient {
            height: 6px;
          }
          .icon-container {
            transition: transform 0.2s ease;
          }
          .stats-card:hover .icon-container {
            transform: scale(1.05);
          }
          .table > :not(caption) > * > * {
            padding: 0.75rem 0.75rem;
            vertical-align: middle;
          }
          @media (max-width: 767.98px) {
            .table td, .table th {
              padding: 0.5rem;
            }
            .btn-sm {
              padding: 0.25rem 0.5rem;
              font-size: 0.75rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default FraudAlerts;